import {
  GeographicTrainMapTopology,
  Train,
  TrainMap,
} from "@vygruppen/vy-train-map";
import { useAffectedTrainsForMap } from "features/CenterContent/RoleContent/TrainMap/useAffectedTrainsForMap";
import { useInfrastructureInformation } from "features/CenterContent/RoleContent/TrainMap/useInfrastructureInformation";
import { useRealtimeTrains } from "features/CenterContent/RoleContent/TrainMap/useRealtimeTrains";
import { useStationInformation } from "features/CenterContent/RoleContent/TrainMap/useStationInformation";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { createToast } from "shared/components/Spor";
import * as ROUTES from "shared/utils/routes";
import { useDropsRole } from "stores/useDropsRole";
import { useEventMode, useStretchBuilder } from "stores/useStretchBuilder";
import { useTheme } from "styled-components";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

type TrainMapProps = {
  topology: GeographicTrainMapTopology;
  onEventClick: (ids: { infrastructure: string[]; station: string[] }) => void;
};

export const TrainMapRenderer: FC<TrainMapProps> = ({
  topology,
  onEventClick,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const eventMode = useEventMode((state) => state.eventMode);

  const [
    clickedStops,
    setClickedStops,
    selectedLegs,
    selectedStops,
    allStopsSelected,
    setSelectedLegs,
    setSelectedStops,
  ] = useStretchBuilder((state) => [
    state.clickedStops,
    state.setClickedStops,
    state.selectedLegs,
    state.selectedStops,
    state.allStopsSelected,
    state.setSelectedLegs,
    state.setSelectedStops,
  ]);

  const { infraInfo, isInfraInfoSuccess, handleInfraWebsocketMessage } =
    useInfrastructureInformation();

  const { stationInfo, isStationInfoSuccess, handleStationWebsocketMessage } =
    useStationInformation();

  useWebsocket(BackendIdentifier.TRAINMAP_BACKEND, {
    [WebsocketTopic.INFRASTRUCTURE_EVENT]: handleInfraWebsocketMessage,
  });

  useWebsocket(BackendIdentifier.DROPS_BACKEND, {
    [WebsocketTopic.STATION_EVENT]: handleStationWebsocketMessage,
  });

  const infraEventIds = infraInfo?.infrastructure_information.map(
    (info) => info.event.incident_id,
  );

  const { affectedTrains, isAffectedTrainsSuccess } = useAffectedTrainsForMap(
    infraEventIds,
    isInfraInfoSuccess,
  );

  const {
    isVaktlederOrDriftledningen,
    isVaktlederOrDriftledningenOrTrafikkplanlegger,
  } = useDropsRole();

  return (
    <TrainMap
      topology={topology}
      useTrains={useRealtimeTrains}
      infrastructureInformation={
        isInfraInfoSuccess && infraInfo
          ? infraInfo
          : { infrastructure_information: [] }
      }
      stationInformation={
        isStationInfoSuccess && stationInfo
          ? stationInfo
          : { station_information: [] }
      }
      affectedTrains={
        isAffectedTrainsSuccess && affectedTrains
          ? affectedTrains
          : { trains_for_event: [] }
      }
      theme={theme.themeName as "light" | "dark"}
      stretchBuilder={
        isVaktlederOrDriftledningenOrTrafikkplanlegger()
          ? {
              enabled: isVaktlederOrDriftledningen() ? eventMode : false,
              shouldClearSelectedStretches:
                clickedStops.length === 0 && selectedLegs.length === 0,
              selectedLegs,
              selectedStops,
              clickedStops,
              setClickedStops,
              setSelectedLegs,
              setSelectedStops,
              eventOnClick: onEventClick,
              allStopsSelected,
            }
          : undefined
      }
      trainOnClick={(train: Train) => {
        if (
          (train.train_id &&
            train.train_date &&
            ["VY", "VYT", "RV"].includes(train.train_company ?? "")) ||
          !train.train_company
        ) {
          navigate(
            generatePath(ROUTES.TRAIN, {
              countryCode: train.country_code ?? null,
              trainNumber: train.identifier,
              trainDate: train.train_date,
            }),
          );
        } else {
          createToast({
            variant: "info",
            text: `Har ikke data på tog ${train.train_id} fra ${train.train_company}`,
          });
        }
      }}
      language={i18n.language === "se" ? "se" : "no"}
    />
  );
};
